import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Image,
  Menu,
  Modal,
  Radio,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconArrowLeft,
  IconCopy,
  IconDotsVertical,
  IconLayoutCards,
  IconMessages,
  IconPencil,
  IconStar,
  IconThumbDown,
  IconThumbUp,
  IconUser,
} from '@tabler/icons-react';
import { useState } from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import getErrorMessage from '../../helpers/getErrorMessage';
import useFetch from '../../helpers/useFetch';
import { AnswerTypeBadge } from './ConversationList';
import {
  formatListDate,
  formatMessageTime,
  formatPrice,
  parseAssistantText,
  profileHref,
  shopperLabel,
  withoutTrackingParams,
} from './conversationFormat';
import classes from './Conversations.module.css';

const MarkdownLink = ({ href, children }) => (
  <a href={href} target="_blank" rel="noreferrer noopener">
    {children}
  </a>
);

const markdownComponents = { a: MarkdownLink };

const transformUrl = (url) => defaultUrlTransform(withoutTrackingParams(url));

const copyToClipboard = (value) => navigator.clipboard?.writeText(value)?.catch(() => {});

export const ProductCard = ({ product }) => {
  const price = formatPrice(product.price, product.currency);
  const listPrice = formatPrice(product.listPrice, product.currency);

  return (
    <Card withBorder radius="md" padding="sm" className={classes.productCard}>
      <Card.Section>
        <Image src={product.imageUrl} alt={product.name || ''} fit="contain" className={classes.productImage} />
      </Card.Section>
      <Text size="sm" fw={600} lineClamp={2} mt="sm">
        {product.name}
      </Text>
      {price && (
        <Group gap={6} mt={4}>
          <Text size="sm" fw={700}>
            {price}
          </Text>
          {listPrice && (
            <Text size="xs" c="dimmed" className={classes.listPrice}>
              {listPrice}
            </Text>
          )}
        </Group>
      )}
      {product.url && (
        <Button
          component="a"
          href={transformUrl(product.url)}
          target="_blank"
          rel="noreferrer noopener"
          color="dark"
          size="xs"
          fullWidth
          mt="sm"
          tt="uppercase"
        >
          View product
        </Button>
      )}
    </Card>
  );
};

// The shopper's vote on this reply (1 or -1); no vote, no icon.
const VoteIcon = ({ vote }) => {
  if (vote === 1) return <IconThumbUp size={14} color="var(--mantine-color-green-7)" aria-label="Shopper voted thumbs up" />;
  if (vote === -1) return <IconThumbDown size={14} color="var(--mantine-color-red-7)" aria-label="Shopper voted thumbs down" />;
  return null;
};

export const AssistantMessage = ({ message, actions, highlighted }) => {
  const { text, productFeeds, suggestions } = parseAssistantText(message.text);
  const products = message.products || [];

  return (
    <Stack gap={6} align="flex-start">
      <Box className={classes.messageColumn} data-highlighted={highlighted || undefined}>
        {text && (
          <Box className={`${classes.assistantBubble} ${classes.markdown}`}>
            <ReactMarkdown components={markdownComponents} urlTransform={transformUrl}>
              {text}
            </ReactMarkdown>
          </Box>
        )}
        <Group gap={6} mt={4}>
          <Text size="xs" c="dimmed">
            {formatMessageTime(message.timestamp)}
          </Text>
          <AnswerTypeBadge type={message.messageType} size="xs" />
          <VoteIcon vote={message.vote} />
          {actions}
        </Group>
      </Box>
      {products.length > 0 && (
        <Group gap="sm" align="stretch">
          {products.map((product, index) => (
            <ProductCard key={product.id || index} product={product} />
          ))}
        </Group>
      )}
      {productFeeds.length > 0 && (
        <Group gap={6}>
          {productFeeds.map((title, index) => (
            <Badge key={`${title}-${index}`} variant="outline" color="gray" leftSection={<IconLayoutCards size={12} />}>
              Product carousel: {title}
            </Badge>
          ))}
        </Group>
      )}
      {suggestions.length > 0 && (
        <Group gap={6}>
          {suggestions.map((suggestion, index) => (
            <Badge key={`${suggestion}-${index}`} variant="outline" color="blue" tt="none" radius="xl">
              {suggestion}
            </Badge>
          ))}
        </Group>
      )}
    </Stack>
  );
};

export const ShopperMessage = ({ message }) => (
  <Stack gap={4} align="flex-end">
    <Box className={classes.messageColumn}>
      <Text size="sm" className={classes.shopperBubble}>
        {message.text}
      </Text>
    </Box>
    <Text size="xs" c="dimmed">
      {formatMessageTime(message.timestamp)}
    </Text>
  </Stack>
);

const ConversationHeader = ({ session, accountId, domainId, onBack }) => {
  const href = profileHref(accountId, domainId, session.shopper?.profileId);
  const messages = session.messages || [];
  const questionCount = messages.filter((message) => message.sender === 'shopper').length;
  const answerTypes = [...new Set(messages.map((message) => message.messageType).filter(Boolean))].sort();
  const title = shopperLabel(session.shopper);

  return (
    <Group className={classes.paneHeader} justify="space-between" wrap="nowrap" gap="sm">
      <Group gap="xs" wrap="nowrap" miw={0}>
        <ActionIcon
          variant="subtle"
          className={classes.mobileOnly}
          onClick={onBack}
          aria-label="All conversations"
          title="All conversations"
        >
          <IconArrowLeft size={18} />
        </ActionIcon>
        <Stack gap={0} miw={0}>
          <Text fw={600} lineClamp={1}>
            {title}
          </Text>
          <Text size="xs" c="dimmed" lineClamp={1}>
            {[
              session.shopper?.email && session.shopper.email !== title ? session.shopper.email : null,
              `${questionCount} ${questionCount === 1 ? 'question' : 'questions'}`,
              `${messages.length} ${messages.length === 1 ? 'message' : 'messages'}`,
              `started ${formatListDate(session.startedAt)}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Text>
        </Stack>
      </Group>
      <Group gap="xs" wrap="nowrap">
        <Group gap={4} wrap="nowrap" className={classes.desktopOnly}>
          {answerTypes.map((type) => (
            <AnswerTypeBadge key={type} type={type} size="xs" />
          ))}
        </Group>
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon variant="outline" radius="xl" size="lg" aria-label="Conversation actions">
              <IconDotsVertical size={18} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            {href && (
              <Menu.Item component="a" href={href} leftSection={<IconUser size={14} />}>
                Open profile
              </Menu.Item>
            )}
            <Menu.Item leftSection={<IconCopy size={14} />} onClick={() => copyToClipboard(session.chatSessionId)}>
              Copy conversation ID
            </Menu.Item>
            {session.shopper?.visitorId && (
              <Menu.Item
                leftSection={<IconCopy size={14} />}
                onClick={() => copyToClipboard(session.shopper.visitorId)}
              >
                Copy visitor ID
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Group>
  );
};

export const ConversationPlaceholder = () => (
  <Stack align="center" justify="center" gap="xs" h="100%" p="xl" ta="center">
    <IconMessages size={40} stroke={1} color="var(--mantine-color-gray-5)" />
    <Text fw={500}>Select a conversation</Text>
    <Text size="sm" c="dimmed">
      Pick a conversation on the left to read what the shopper asked and how the sales agent answered.
    </Text>
  </Stack>
);

// A rewritten answer (knowledge) or a rule (playbook) for one reply, sent to the Learning inbox.
const SuggestAnswerModal = ({ question, message, onSend, onClose }) => {
  const agentText = parseAssistantText(message.text).text;
  const [target, setTarget] = useState('knowledge');
  const [answer, setAnswer] = useState(agentText);
  const [rule, setRule] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const text = (target === 'playbook' ? rule : answer).trim();

  const send = () => {
    setSending(true);
    onSend({ messageId: message.messageId, kind: 'fix', target, text, ...(note.trim() && { note: note.trim() }) })
      .then(() => {
        notifications.show({ message: 'Sent for review in Learning', color: 'success' });
        onClose();
      })
      .catch((error) => {
        setSending(false);
        notifications.show({ title: 'Could not send', message: getErrorMessage(error), color: 'danger' });
      });
  };

  return (
    <Modal opened onClose={onClose} title="Suggest a better answer" size="lg" centered>
      <Stack gap="sm">
        <Textarea label="Shopper question" value={question} readOnly autosize minRows={1} />
        <Box>
          <Text size="sm" fw={500}>
            Agent answer
          </Text>
          <Text size="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap' }}>
            {agentText}
          </Text>
        </Box>
        <Radio.Group label="What should change?" value={target} onChange={setTarget}>
          <Stack gap={6} mt={6}>
            <Radio value="knowledge" label="What the agent knows (knowledge)" />
            <Radio value="playbook" label="How the agent behaves (playbook)" />
          </Stack>
        </Radio.Group>
        {target === 'playbook' ? (
          <Textarea
            label="Rule for the agent"
            required
            autosize
            minRows={2}
            maxLength={500}
            value={rule}
            onChange={(e) => setRule(e.currentTarget.value)}
          />
        ) : (
          <Textarea
            label="Better answer"
            required
            autosize
            minRows={4}
            maxLength={4000}
            value={answer}
            onChange={(e) => setAnswer(e.currentTarget.value)}
          />
        )}
        <Text size="xs" c="dimmed">
          Write it in your own words — a super admin reviews it, and the AI rewrites it in the store&apos;s language
          before it goes live.
        </Text>
        <Textarea
          label="Why"
          description="Optional."
          autosize
          minRows={1}
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
        />
      </Stack>
      <Group justify="flex-end" gap={8} mt="md">
        <Button variant="default" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={!text} loading={sending} onClick={send}>
          Send for review
        </Button>
      </Group>
    </Modal>
  );
};

export const messageKey = (message, index) =>
  message.messageId ? `${message.sender}-${message.messageId}` : `${message.timestamp}-${index}`;

const ConversationDetail = ({ session, accountId, domainId, onBack, fetchUrl }) => {
  const fetch = useFetch();
  const [editing, setEditing] = useState(null);
  const [promotingId, setPromotingId] = useState(null);
  const messages = session.messages || [];

  const sendCorrection = (body) =>
    fetch(`${fetchUrl}/chatBot/learning/corrections`, {
      method: 'POST',
      body: JSON.stringify({ chatSessionId: session.chatSessionId, ...body }),
    });

  const promote = (message) => {
    setPromotingId(message.messageId);
    sendCorrection({ messageId: message.messageId, kind: 'promote' })
      .then(() => notifications.show({ message: 'Saved for review in Learning', color: 'success' }))
      .catch((error) =>
        notifications.show({ title: 'Could not save', message: getErrorMessage(error), color: 'danger' })
      )
      .finally(() => setPromotingId(null));
  };

  // The API pairs a reply with the nearest shopper message before it; without one there is nothing to correct.
  const actionsFor = (message, index) => {
    const question = messages.slice(0, index).findLast((previous) => previous.sender === 'shopper')?.text;
    if (!fetchUrl || !message.messageId || !question) return null;
    return (
      <>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          aria-label="Suggest a better answer"
          title="Suggest a better answer"
          onClick={() => setEditing({ message, question })}
        >
          <IconPencil size={14} />
        </ActionIcon>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          aria-label="Promote this answer"
          title="Promote this answer"
          loading={promotingId === message.messageId}
          onClick={() => promote(message)}
        >
          <IconStar size={14} />
        </ActionIcon>
      </>
    );
  };

  return (
    <>
      <ConversationHeader session={session} accountId={accountId} domainId={domainId} onBack={onBack} />
      <Box className={classes.paneBody}>
        <Stack gap="lg" className={classes.transcript}>
          {messages.map((message, index) =>
            message.sender === 'shopper' ? (
              <ShopperMessage key={messageKey(message, index)} message={message} />
            ) : (
              <AssistantMessage
                key={messageKey(message, index)}
                message={message}
                actions={actionsFor(message, index)}
              />
            )
          )}
        </Stack>
      </Box>
      {editing && (
        <SuggestAnswerModal
          key={editing.message.messageId}
          question={editing.question}
          message={editing.message}
          onSend={sendCorrection}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
};

export default ConversationDetail;
